import { FC, useCallback, useContext, useEffect, useState } from 'react';

import { Flex, Row, Select } from 'antd';
import { DefaultOptionType } from 'antd/es/select';

import { ActionButton } from '@hdi-seguros/web-components.action-button';

import { QuotationInfoContext } from '../../contexts';

import {
  Article,
  ArticleBranch,
  GetQuotationGraphicInfoServiceResponse,
  Group,
  SelectOption,
} from '../../../../../../../shared/interfaces';

import { QuotationGroups } from '../../../utils/quotation-groups.enum';

import { displayNotImplementedMessage } from '../../../../../../../shared/utils';
import { QuotationInfo } from './components/QuotationInfo';
import QuotationService from '../../../../../../../shared/services/quotation';
import { ColSize } from '../../../../../../../shared/components';

interface QuotationGroupSelectorProps {
  groups: Array<Group>;
  groupsList: Array<SelectOption>;
  isLoadingGroupsList: boolean;
  onInitQuotation: (
    article: string,
    branch: string,
    arse: string,
    group: string
  ) => Promise<void>;
}

const { 
    AP, 
    AUTOMOVILES,
    INCENDIO, 
    VIDA_OBLIGATORIA, 
    RESPONSABILIDAD_CIVIL, 
    INTEGRAL_CONSORCIO, 
    INTEGRAL_COMERCIO, 
    HOGAR, 
    EFECTOS_PERSONALES,
    ROBO
} = QuotationGroups;

const QUOTATION_GROUPS: Array<QuotationGroups> = [ 
    AP, 
    AUTOMOVILES, 
    INCENDIO, 
    VIDA_OBLIGATORIA, 
    RESPONSABILIDAD_CIVIL, 
    INTEGRAL_CONSORCIO, 
    INTEGRAL_COMERCIO, 
    HOGAR,
    EFECTOS_PERSONALES,
    ROBO,
];

export const QuotationGroupSelector: FC<QuotationGroupSelectorProps> = ({
  groups = [],
  groupsList = [],
  isLoadingGroupsList,
  onInitQuotation,
}) => {
  const [articles, setArticles] = useState<Array<Article>>([]);
  const [articleSelected, setArticleSelected] = useState<string | undefined>();
  const [articlesList, setArticlesList] = useState<Array<SelectOption>>([]);
  const [branchSelected, setBranchSelected] = useState<
    ArticleBranch | undefined
  >();
  const [groupSelected, setGroupSelected] = useState<QuotationGroups>();
  const [isLoadingArticles, setIsLoadingArticles] = useState(false);

  const [quotationGraphicInfo, setQuotationGraphicInfo] = useState<GetQuotationGraphicInfoServiceResponse>();

  const { setAllArticleSelected } = useContext(QuotationInfoContext);

  /* 
        ? Cuando se implementen el resto de los cotizadores hay que remover esta validación. 
        ? Actualmente lo unico que hace es validar si el grupo seleccionado es el de AP.
    */
  const onPressInitNewQuotation = useCallback(async () => {
    if (QUOTATION_GROUPS.includes(groupSelected!)) {
      await onInitQuotation(
        articleSelected!,
        branchSelected?.codigo!,
        branchSelected?.secRamaArticulo!,
        groupSelected!
      );

      return;
    }

    displayNotImplementedMessage();
  }, [groupSelected, onInitQuotation, articleSelected, branchSelected]);

  const onSelectArticle = useCallback(
    (value: string) => {
      const selectedBranch = articles.find((el) => el.codigo === value);
      
      setAllArticleSelected(selectedBranch);
      setBranchSelected(selectedBranch?.ramas?.rama[0]);
      setArticleSelected(value);
    },
    [articles, setArticleSelected, setBranchSelected]
  );

  const onSelectGroup = useCallback(
    (groupCode: string) => {
      setGroupSelected(groupCode as QuotationGroups);
      setArticleSelected(undefined);
      setIsLoadingArticles(true);

      const articlesByGroup =
        groups.find((group) => group.codigo === groupCode)?.articulos || [];

      const filterArticles = articlesByGroup.filter(
        (el) => el?.codigo !== undefined || el?.descLarga !== undefined
      );

      setArticles(filterArticles);

      const articlesList = filterArticles.map(({ codigo, descLarga }) => ({
        label: descLarga!,
        value: codigo!,
      }));
      setArticlesList(articlesList);

      setIsLoadingArticles(false);
    },
    [setGroupSelected, setArticleSelected, setIsLoadingArticles, groups]
  );

  const getQuotationGraphicInfo = useCallback(async () => {
    try {
      const response = await QuotationService.getQuotationGraphicInfo( articleSelected!, groupSelected! );
      if(response.data) {
        setQuotationGraphicInfo(response.data);
      }
    } catch (error) {}
  }, [articleSelected, groupSelected]);

  const filterSort = (
    optionA: DefaultOptionType,
    optionB: DefaultOptionType
  ) =>
    String(optionA?.label ?? '')
      .toLowerCase()
      .localeCompare(String(optionB?.label ?? '').toLowerCase())

  useEffect(() => {
    if(articleSelected && groupSelected) {
      getQuotationGraphicInfo()
    }
  }, [articleSelected, groupSelected]);

  useEffect(() => {
    if (articlesList?.length === 1) {
      onSelectArticle(articlesList[0].value);
    }
  }, [articlesList, onSelectArticle]);

  return (
    <Flex vertical gap={16}>
      <Row wrap style={{ rowGap: 10 }}>
        <ColSize size={25} colProps={{ style: { paddingRight: 15 }, }}>
          <Select
            optionFilterProp='label'
            showSearch
            filterSort={filterSort}
            options={groupsList}
            loading={isLoadingGroupsList}
            placeholder='Seleccione un grupo'
            onSelect={onSelectGroup}
            style={{ width: '100%' }}
          />
        </ColSize>

        <ColSize size={25} colProps={{ style: { paddingRight: 15 }, }}>
          <Select
            options={articlesList}
            placeholder='Seleccione un artículo'
            disabled={!articlesList.length}
            loading={isLoadingArticles}
            onSelect={onSelectArticle}
            value={articleSelected}
            style={{ width: '100%' }}
          />
        </ColSize>

        <ColSize size={10}>
          <ActionButton
            className='btn-principal-barbuss'
            label='Iniciar Cotización'
            onClick={onPressInitNewQuotation}
            disabled={!articleSelected?.length}
            style={{ width: '100%' }}
          />
        </ColSize>
      </Row>

      {
        quotationGraphicInfo && (
          <QuotationInfo quotationGraphicInfo={quotationGraphicInfo} />
        )
      }
      
    </Flex>
  );
};
